import { styled as muiStyled, css } from '@mui/material/styles';
import * as React from 'react';

import Arranger from '../arranger';
import Spacer, { spc } from '../spacer';
import TextField from '../text-field';
interface OTPProps {
  separator: React.ReactNode;
  length: number;
  value: string;
  onChange: React.Dispatch<React.SetStateAction<string>>;
}
const OTPInputImpl = ({ separator, length, value, onChange }: OTPProps) => {
  const inputRefs = React.useRef<HTMLInputElement[]>(
    new Array(length).fill(null)
  );

  const focusInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.focus();
  };

  const selectInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.select();
  };

  /**
   * Handles key down events in the OTP inputs.
   * - Prevents default behavior for arrow keys, delete, backspace and spacebar.
   * - Moves focus between inputs on left/right arrow key presses.
   * - Deletes previous character on delete and backspace.
   * - Calls onChange to update OTP value in state on delete and backspace.
   */
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    currentIndex: number
  ) => {
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case ' ':
        event.preventDefault();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (currentIndex < length - 1) {
          focusInput(currentIndex + 1);
          selectInput(currentIndex + 1);
        }
        break;
      case 'Delete':
        event.preventDefault();
        onChange((prevOtp) => {
          const otp =
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });
        break;
      case 'Backspace':
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        onChange((prevOtp) => {
          const otp =
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });
        break;

      default:
        break;
    }
  };

  /**
   * Handles input change events for the OTP inputs.
   * Updates the OTP value in state by inserting the new input character
   * in the correct index based on the currentIndex.
   * Focuses the next input if the current input was filled.
   */
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    currentIndex: number
  ) => {
    const currentValue = event.target.value.replace(/\D/g, '');
    if (!currentValue && event.target.value) {
      return;
    }
    let indexToEnter = 0;

    while (indexToEnter <= currentIndex) {
      if (
        inputRefs.current[indexToEnter].value &&
        indexToEnter < currentIndex
      ) {
        indexToEnter += 1;
      } else {
        break;
      }
    }
    onChange((prev) => {
      const otpArray = prev.split('');
      const lastValue = currentValue[currentValue.length - 1];
      otpArray[indexToEnter] = lastValue;
      return otpArray.join('');
    });
    if (currentValue !== '') {
      if (currentIndex < length - 1) {
        focusInput(currentIndex + 1);
      }
    }
  };

  const handleClick = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent>,
    currentIndex: number
  ) => {
    selectInput(currentIndex);
  };

  /**
   * Handles pasting text into the OTP inputs.
   * Prevents the default paste behavior.
   * Gets the pasted text from the clipboard.
   * Trims the pasted text to match the OTP length.
   * Inserts the trimmed text into the OTP value,
   * starting at the index of the currently focused input.
   * Updates the OTP value state with the new pasted text.
   */
  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    currentIndex: number
  ) => {
    event.preventDefault();
    const clipboardData = event.clipboardData;

    // Check if there is text data in the clipboard
    if (clipboardData.types.includes('text/plain')) {
      let pastedText = clipboardData.getData('text/plain');
      pastedText = pastedText.replace(/\D/g, '').substring(0, length);
      let indexToEnter = 0;

      while (indexToEnter <= currentIndex) {
        if (
          inputRefs.current[indexToEnter].value &&
          indexToEnter < currentIndex
        ) {
          indexToEnter += 1;
        } else {
          break;
        }
      }

      const otpArray = value.split('');

      for (let i = indexToEnter; i < length; i += 1) {
        const lastValue = pastedText[i - indexToEnter] ?? '';
        otpArray[i] = lastValue;
      }

      onChange(otpArray.join(''));
    }
  };

  return (
    <Arranger alignItems="center" justify="center">
      {' '}
      {new Array(length).fill(null).map((_, index) => (
        <React.Fragment key={index}>
          <TextField
            aria-label={`Digit ${index + 1} of OTP`}
            inputRef={(ele) => (inputRefs.current[index] = ele!)}
            onKeyDown={(event: any) => {
              handleKeyDown(event, index);
            }}
            onChange={(event: any) => {
              handleChange(event, index);
            }}
            onClick={(event: any) => {
              handleClick(event, index);
            }}
            onPaste={(event: any) => {
              handlePaste(event, index);
            }}
            value={value[index] ?? ''}
            className="otp-field"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
          />
          <Spacer pl={spc.xxs} />
          {index === length - 1 ? null : separator}
          <Spacer pl={spc.xxs} />
        </React.Fragment>
      ))}
    </Arranger>
  );
};
export const OTPInput = muiStyled(OTPInputImpl)(
  ({ theme }) => css`
    .otp-field {
      min-width: 32px;

      input {
        text-align: center;
        font-size: clamp(18px, 4vw, 30px);
        padding: ${theme.spacing(1)} ${theme.spacing(0.5)};
      }
    }
  `
);

export default OTPInput;
